import type { Stage } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger'

export const STAGES: readonly Stage[] = [
  'generation_queued',
  'generation_running',
  'awaiting_approval',
  'approved',
  'generation_failed',
  'rejected',
  'cancelled',
] as const

export const STAGE_LABELS: Record<Stage, string> = {
  generation_queued: 'Queued',
  generation_running: 'Running',
  awaiting_approval: 'Awaiting Approval',
  approved: 'Approved',
  generation_failed: 'Failed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<Stage, ChipVariant> = {
  generation_queued: 'neutral',
  generation_running: 'neutral',
  awaiting_approval: 'warning',
  approved: 'success',
  generation_failed: 'danger',
  rejected: 'danger',
  cancelled: 'neutral',
}

export const STAGE_ACCENT: Record<Stage, 'neutral' | 'success' | 'warning' | 'danger'> = {
  generation_queued: 'neutral',
  generation_running: 'neutral',
  awaiting_approval: 'warning',
  approved: 'success',
  generation_failed: 'danger',
  rejected: 'danger',
  cancelled: 'neutral',
}

/**
 * User-driven transitions. Drags or Shift+l/h only fire when the (from, to)
 * pair is in this set — everything else is owned by the Celery pipeline or
 * terminal. Each transition maps to exactly one mutation endpoint.
 */
export type MutationAction = 'approve' | 'reject' | 'retry'

export interface Transition {
  from: Stage
  to: Stage
  action: MutationAction
}

export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  { from: 'awaiting_approval', to: 'approved', action: 'approve' },
  { from: 'awaiting_approval', to: 'rejected', action: 'reject' },
  { from: 'generation_running', to: 'rejected', action: 'reject' },
  { from: 'generation_failed', to: 'generation_queued', action: 'retry' },
] as const

export function transitionFor(from: Stage, to: Stage): Transition | null {
  return ALLOWED_TRANSITIONS.find((t) => t.from === from && t.to === to) ?? null
}

/** "Natural next" when the user presses Shift+l on an advanceable card. */
export function naturalForward(stage: Stage): Transition | null {
  if (stage === 'awaiting_approval') return transitionFor('awaiting_approval', 'approved')
  if (stage === 'generation_failed')
    return transitionFor('generation_failed', 'generation_queued')
  return null
}

/** "Natural back" — Shift+h — sends awaiting to rejected (the only
 *  backward-ish user action). No-op for other stages. */
export function naturalBackward(stage: Stage): Transition | null {
  if (stage === 'awaiting_approval') return transitionFor('awaiting_approval', 'rejected')
  if (stage === 'generation_running') return transitionFor('generation_running', 'rejected')
  return null
}

export function ageLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${Math.max(0, Math.floor(seconds))}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d`
  return `${Math.floor(seconds / (7 * 86400))}w`
}

export function ageVariant(seconds: number | null | undefined): ChipVariant {
  if (seconds === null || seconds === undefined) return 'neutral'
  if (seconds > 7 * 86400) return 'danger'
  if (seconds > 86400) return 'warning'
  return 'neutral'
}
